import { FC, useContext, useEffect, useRef } from "react";
import { SessionContext } from "@app/pippa/session";
import {
  APIContext,
  DialogsContext,
  EventsContext,
  PippaContext,
} from "@app/pippa/provider";
import { introMessage } from "@app/pippa/models/constants";

const SessionInactivityWatcher: FC = () => {
  const { isWidgetOpen } = useContext(PippaContext);
  const sessionController = useContext(SessionContext);
  const { clearChatSession } = useContext(APIContext);
  const {
    replaceMessages,
    setIsInputDisabled,
    setIsInputHidden,
    userInputRef,
  } = useContext(EventsContext);
  const { acknowledgementDialog, closeWidgetDialog, messageFeedbackDialog } =
    useContext(DialogsContext);
  const { open: openAcknowledgementDialog } = acknowledgementDialog;
  const { close: closeCloseWidgetDialog } = closeWidgetDialog;
  const { close: closeMessageFeedbackDialog } = messageFeedbackDialog;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const { getSession, isActiveSession, getSessionTtlMs, clearStorage } =
      sessionController;

    if (!isWidgetOpen) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    const session = getSession();
    const ttlMs = getSessionTtlMs();

    if (!session || !isActiveSession(session)) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    const expireAt = session.lastActivityAt + ttlMs;
    const delayMs = Math.max(0, expireAt - Date.now());

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      clearStorage();
      clearChatSession();
      replaceMessages([introMessage]);
      setIsInputDisabled(false);
      setIsInputHidden(false);
      userInputRef?.current?.clearInput();
      closeMessageFeedbackDialog();
      closeCloseWidgetDialog();
      openAcknowledgementDialog({});
    }, delayMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    isWidgetOpen,
    sessionController,
    clearChatSession,
    replaceMessages,
    openAcknowledgementDialog,
    closeCloseWidgetDialog,
    closeMessageFeedbackDialog,
  ]);

  return null;
};

export default SessionInactivityWatcher;
